import React from 'react';
import { useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import { colorPalette } from 'shared/constants';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { Modal } from 'src/components/design-system';

// The app-wide error color is a pale pink that reads as disabled on a filled button.
const SOLID_DANGER_THEME = { token: { colorError: colorPalette.redMedium } };

type ReplaceConfirmModalProps = {
  open: boolean;
  removedCount: number;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Asks the admin to confirm a Replace import, showing how many contacts the dry run will remove.
 *
 * @param props.open - Whether the dialog is shown.
 * @param props.removedCount - Contacts the Replace removes, from the dry run's `deleted`.
 * @param props.loading - True while the real Replace runs; locks the dialog.
 * @param props.onCancel - Closes the dialog without importing.
 * @param props.onConfirm - Runs the real Replace.
 * @returns The confirmation dialog.
 */
export const ReplaceConfirmModal = ({
  open,
  removedCount,
  loading,
  onCancel,
  onConfirm,
}: ReplaceConfirmModalProps): React.ReactElement => {
  const lang = useSelector(getLang('SETTINGS'));
  const description =
    removedCount === 1 ? lang.REPLACE_CONFIRM_DESCRIPTION_ONE : lang.REPLACE_CONFIRM_DESCRIPTION;

  return (
    <ConfigProvider theme={SOLID_DANGER_THEME}>
      <Modal
        open={open}
        title={lang.REPLACE_CONFIRM_TITLE}
        onCancel={onCancel}
        onSubmit={onConfirm}
        cancelText={lang.CANCEL}
        submitText={lang.UPLOAD_MODE_REPLACE}
        submitDanger
        loading={loading}>
        <p>{description.replace('{count}', formatNumber(removedCount))}</p>
        <p>{lang.REPLACE_KEEPS_USERS}</p>
      </Modal>
    </ConfigProvider>
  );
};
